import { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "@/config";
import { Calendar, Clock, User, Loader2, ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/card";

const http = axios.create({ baseURL: API_URL, withCredentials: true });

interface Appointment {
    dlr_appt_id: number;
    dlr_appt_did: number;
    dlr_appt_title: string;
    dlr_appt_notes: string | null;
    dlr_appt_starttime_humanread: string | null;
    dlr_appt_salesname_txt: string | null;
    dlr_appt_created_at: string;
}

interface Dealer { id: number; company: string; }

export default function BdcAppointmentsPage() {
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [dealers,      setDealers]      = useState<Dealer[]>([]);
    const [loading,      setLoading]      = useState(true);
    const [dealerF,      setDealerF]      = useState<number | "ALL">("ALL");
    const [search,       setSearch]       = useState("");
    const [expanded,     setExpanded]     = useState<number | null>(null);

    useEffect(() => {
        Promise.all([
            http.get("/bdc/appointments").then(r => r.data).catch(() => []),
            http.get("/bdc/dealers").then(r => r.data).catch(() => []),
        ]).then(([a, d]) => {
            setAppointments(a);
            setDealers(d);
        }).finally(() => setLoading(false));
    }, []);

    const today = new Date().toDateString();
    const todayCount = appointments.filter((a) => new Date(a.dlr_appt_created_at).toDateString() === today).length;

    const visible = appointments.filter((a) => {
        const matchDealer = dealerF === "ALL" || a.dlr_appt_did === dealerF;
        const q = search.toLowerCase();
        const matchSearch = !q || `${a.dlr_appt_title} ${a.dlr_appt_salesname_txt ?? ""} ${a.dlr_appt_notes ?? ""}`.toLowerCase().includes(q);
        return matchDealer && matchSearch;
    });

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Appointments</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                    {loading ? "Loading…" : `${appointments.length} total · ${todayCount} scheduled today`}
                </p>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-3 gap-4">
                {[
                    { label: "Total Appointments", value: appointments.length,               icon: Calendar, color: "text-blue-500",   bg: "bg-blue-50"   },
                    { label: "Scheduled Today",     value: todayCount,                       icon: Clock,    color: "text-orange-500", bg: "bg-orange-50" },
                    { label: "Dealers",             value: dealers.length,                   icon: User,     color: "text-violet-500", bg: "bg-violet-50" },
                ].map(({ label, value, icon: Icon, color, bg }) => (
                    <Card key={label} className="p-4">
                        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                            <Icon className={`h-5 w-5 ${color}`} />
                        </div>
                        <p className="text-2xl font-bold">{loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : value}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                    </Card>
                ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 items-center">
                <input
                    type="text"
                    placeholder="Search title, salesperson, notes…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border rounded-lg px-3 py-1.5 text-sm flex-1 min-w-48 max-w-xs"
                />
                <select
                    value={String(dealerF)}
                    onChange={(e) => setDealerF(e.target.value === "ALL" ? "ALL" : Number(e.target.value))}
                    className="border rounded-lg px-3 py-1.5 text-sm"
                >
                    <option value="ALL">All Dealers</option>
                    {dealers.map((d) => <option key={d.id} value={d.id}>{d.company}</option>)}
                </select>
            </div>

            {/* Appointments list */}
            <Card className="overflow-hidden">
                {loading ? (
                    <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : visible.length === 0 ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">No appointments found</div>
                ) : (
                    <div className="divide-y">
                        {visible.map((a) => {
                            const isToday = new Date(a.dlr_appt_created_at).toDateString() === today;
                            const open    = expanded === a.dlr_appt_id;
                            return (
                                <div key={a.dlr_appt_id}>
                                    <button
                                        className="w-full flex items-center justify-between px-5 py-3 hover:bg-muted/40 transition-colors text-left"
                                        onClick={() => setExpanded(open ? null : a.dlr_appt_id)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isToday ? "bg-blue-100" : "bg-gray-100"}`}>
                                                <Calendar className={`h-4 w-4 ${isToday ? "text-blue-600" : "text-gray-400"}`} />
                                            </div>
                                            <div className="text-left">
                                                <p className="text-sm font-medium">{a.dlr_appt_title}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {a.dlr_appt_starttime_humanread ?? new Date(a.dlr_appt_created_at).toLocaleDateString()}
                                                    {a.dlr_appt_salesname_txt && ` · ${a.dlr_appt_salesname_txt}`}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {isToday && (
                                                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Today</span>
                                            )}
                                            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                                        </div>
                                    </button>
                                    {open && a.dlr_appt_notes && (
                                        <div className="px-16 pb-3">
                                            <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">{a.dlr_appt_notes}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>
        </div>
    );
}
